import { APIClientServices } from "./APIClientServices";

/**
 * EK-421 — Knowledge Base client services.
 */

export type KbArticleStatus = "draft" | "published" | "archived";

export interface PublicKbCategoryDTO {
	id: string;
	workspaceId: string;
	parentId: string | null;
	name: string;
	slug: string;
	description: string | null;
	icon: string | null;
	order: number;
	createdAt: string;
	updatedAt: string;
	children?: PublicKbCategoryDTO[];
	articleCount?: number;
}

export interface PublicKbTagDTO {
	id: string;
	workspaceId: string;
	name: string;
	slug: string;
	color: string | null;
	createdAt: string;
}

export interface PublicKbArticleDTO {
	id: string;
	workspaceId: string;
	categoryId: string | null;
	title: string;
	slug: string;
	excerpt: string | null;
	content: string;
	status: KbArticleStatus;
	authorId: string | null;
	viewCount: number;
	readingTimeMinutes: number;
	publishedAt: string | null;
	createdAt: string;
	updatedAt: string;
	category?: PublicKbCategoryDTO | null;
	tags?: PublicKbTagDTO[];
}

export interface ListArticlesFilters {
	status?: KbArticleStatus;
	categoryId?: string;
	tagId?: string;
	search?: string;
	limit?: number;
	offset?: number;
	includeTags?: boolean;
	includeCategory?: boolean;
}

export interface ListArticlesResponse {
	items: PublicKbArticleDTO[];
	total: number;
}

export interface SemanticArticleResult extends PublicKbArticleDTO {
	similarity: number;
}

export interface SemanticSearchResponse {
	items: SemanticArticleResult[];
	total: number;
}

export class KnowledgeBaseServices {
	private static base(workspaceId: string): string {
		return `v1/workspaces/${workspaceId}/kb`;
	}

	// Categories
	static listCategories(workspaceId: string): Promise<PublicKbCategoryDTO[]> {
		return APIClientServices.get<PublicKbCategoryDTO[]>(`${this.base(workspaceId)}/categories`);
	}
	static createCategory(workspaceId: string, body: { name: string; slug?: string; parentId?: string | null; description?: string; icon?: string; order?: number }) {
		return APIClientServices.post<PublicKbCategoryDTO>(`${this.base(workspaceId)}/categories`, body);
	}
	static updateCategory(workspaceId: string, categoryId: string, body: Partial<{ name: string; slug: string; parentId: string | null; description: string; icon: string; order: number }>) {
		return APIClientServices.put<PublicKbCategoryDTO>(`${this.base(workspaceId)}/categories/${categoryId}`, body);
	}
	static deleteCategory(workspaceId: string, categoryId: string): Promise<void> {
		return APIClientServices.delete(`${this.base(workspaceId)}/categories/${categoryId}`);
	}

	// Articles
	static listArticles(workspaceId: string, filters: ListArticlesFilters = {}): Promise<ListArticlesResponse> {
		const params: Record<string, string | number | boolean> = {};
		if (filters.status) params.status = filters.status;
		if (filters.categoryId) params.categoryId = filters.categoryId;
		if (filters.tagId) params.tagId = filters.tagId;
		if (filters.search) params.search = filters.search;
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		if (filters.includeTags) params.includeTags = true;
		if (filters.includeCategory) params.includeCategory = true;
		return APIClientServices.get<ListArticlesResponse>(`${this.base(workspaceId)}/articles`, params);
	}
	static getArticleBySlug(workspaceId: string, slug: string): Promise<PublicKbArticleDTO> {
		return APIClientServices.get<PublicKbArticleDTO>(`${this.base(workspaceId)}/articles/by-slug/${slug}`);
	}
	static createArticle(workspaceId: string, body: { title: string; content: string; slug?: string; categoryId?: string | null; excerpt?: string; status?: KbArticleStatus; tagIds?: string[] }) {
		return APIClientServices.post<PublicKbArticleDTO>(`${this.base(workspaceId)}/articles`, body);
	}
	static updateArticle(workspaceId: string, articleId: string, body: Partial<{ title: string; content: string; slug: string; categoryId: string | null; excerpt: string; status: KbArticleStatus; tagIds: string[] }>) {
		return APIClientServices.put<PublicKbArticleDTO>(`${this.base(workspaceId)}/articles/${articleId}`, body);
	}
	static publishArticle(workspaceId: string, articleId: string) {
		return APIClientServices.post<PublicKbArticleDTO>(`${this.base(workspaceId)}/articles/${articleId}/publish`, {});
	}
	static deleteArticle(workspaceId: string, articleId: string): Promise<void> {
		return APIClientServices.delete(`${this.base(workspaceId)}/articles/${articleId}`);
	}

	// EK-422 — Busca semantica via IA (embeddings + cosine similarity)
	static searchArticlesSemantic(workspaceId: string, query: string, opts: { limit?: number; minSimilarity?: number; includeTags?: boolean; includeCategory?: boolean } = {}): Promise<SemanticSearchResponse> {
		const params: Record<string, string | number | boolean> = { q: query };
		if (opts.limit !== undefined) params.limit = opts.limit;
		if (opts.minSimilarity !== undefined) params.minSimilarity = opts.minSimilarity;
		if (opts.includeTags) params.includeTags = true;
		if (opts.includeCategory) params.includeCategory = true;
		return APIClientServices.get<SemanticSearchResponse>(`${this.base(workspaceId)}/articles/search`, params);
	}

	static backfillEmbeddings(workspaceId: string): Promise<{ scheduled: number }> {
		return APIClientServices.post<{ scheduled: number }>(`${this.base(workspaceId)}/articles/backfill-embeddings`, {});
	}

	// EK-424 — Bulk + reorder + export/import
	static bulkArticleAction(workspaceId: string, articleIds: string[], action: "publish" | "archive" | "delete"): Promise<{ affected: number }> {
		return APIClientServices.post<{ affected: number }>(`${this.base(workspaceId)}/articles/bulk`, { articleIds, action });
	}

	static reorderCategories(workspaceId: string, entries: Array<{ id: string; parentId: string | null; order: number }>): Promise<{ affected: number }> {
		return APIClientServices.post<{ affected: number }>(`${this.base(workspaceId)}/categories/reorder`, { entries });
	}

	static exportWorkspace(workspaceId: string): Promise<KbExportData> {
		return APIClientServices.get<KbExportData>(`${this.base(workspaceId)}/export`);
	}

	static importWorkspace(workspaceId: string, data: KbExportData, mode: "merge" | "replace" = "merge"): Promise<KbImportResult> {
		return APIClientServices.post<KbImportResult>(`${this.base(workspaceId)}/import`, { data, mode });
	}

	// Tags
	static listTags(workspaceId: string): Promise<PublicKbTagDTO[]> {
		return APIClientServices.get<PublicKbTagDTO[]>(`${this.base(workspaceId)}/tags`);
	}
	static createTag(workspaceId: string, body: { name: string; slug?: string; color?: string }) {
		return APIClientServices.post<PublicKbTagDTO>(`${this.base(workspaceId)}/tags`, body);
	}
	static updateTag(workspaceId: string, tagId: string, body: Partial<{ name: string; slug: string; color: string }>) {
		return APIClientServices.put<PublicKbTagDTO>(`${this.base(workspaceId)}/tags/${tagId}`, body);
	}
	static deleteTag(workspaceId: string, tagId: string): Promise<void> {
		return APIClientServices.delete(`${this.base(workspaceId)}/tags/${tagId}`);
	}
}

// EK-424 — DTOs export/import
export interface KbExportData {
	exportedAt: string;
	workspaceId: string;
	categories: PublicKbCategoryDTO[];
	articles: PublicKbArticleDTO[];
	tags: PublicKbTagDTO[];
	articleTags: Array<{ articleId: string; tagId: string }>;
}

export interface KbImportResult {
	categories: number;
	articles: number;
	tags: number;
	articleTags: number;
	skipped: number;
}

// EK-424 — Backoffice global (super-admin MANAGER+)
export interface KbWorkspaceStats {
	workspaceId: string;
	workspaceName: string;
	categoriesCount: number;
	articlesCount: number;
	publishedCount: number;
	tagsCount: number;
	totalViews: number;
	lastPublishedAt: string | null;
}

export class KnowledgeBaseBackofficeServices {
	private static base = "v1/backoffice/knowledge-base";

	static listWorkspacesStats(): Promise<KbWorkspaceStats[]> {
		return APIClientServices.get<KbWorkspaceStats[]>(`${this.base}/workspaces`);
	}

	static listArticles(workspaceId: string, filters: { status?: KbArticleStatus; limit?: number; offset?: number } = {}): Promise<ListArticlesResponse> {
		const params: Record<string, string | number> = {};
		if (filters.status) params.status = filters.status;
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		return APIClientServices.get<ListArticlesResponse>(`${this.base}/workspaces/${workspaceId}/articles`, params);
	}

	static exportWorkspace(workspaceId: string): Promise<KbExportData> {
		return APIClientServices.get<KbExportData>(`${this.base}/workspaces/${workspaceId}/export`);
	}

	static importWorkspace(workspaceId: string, data: KbExportData, mode: "merge" | "replace" = "merge"): Promise<KbImportResult> {
		return APIClientServices.post<KbImportResult>(`${this.base}/workspaces/${workspaceId}/import`, { data, mode });
	}

	static bulkArticleAction(workspaceId: string, articleIds: string[], action: "publish" | "archive" | "delete"): Promise<{ affected: number }> {
		return APIClientServices.post<{ affected: number }>(`${this.base}/workspaces/${workspaceId}/articles/bulk`, { articleIds, action });
	}

	static deleteArticle(workspaceId: string, articleId: string): Promise<void> {
		return APIClientServices.delete(`${this.base}/workspaces/${workspaceId}/articles/${articleId}`);
	}
}
